import { Button, ButtonSizesEnum, ButtonThemesEnum } from '@propertyfinder/button';
import { useDropdownList } from '@propertyfinder/dropdown';
import { domClassMerge } from '@propertyfinder/helpers';
import {
  IconFlag,
  IconSpriteEnvelopeFilled,
  IconSpriteExternalLink,
  IconSpritePhoneFilled,
  IconSpriteThreeDotsVertical,
  IconSpriteWhatsappFilled,
  useIconRtl,
} from '@propertyfinder/icons';
import { Fragment, FunctionComponent, h } from 'preact';
import { useState } from 'preact/hooks';
import { useCallback, useEffect } from 'react';

import { configGetFeature } from 'common/helper/config/get-feature';
import { configGetLanguage } from 'common/helper/config/get-language';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { AgentAvatarSizeEnum } from 'common/module/agent/avatar/size.enum';
import { AgentAvatarTemplate } from 'common/module/agent/avatar/template';
import { AgentAvatarTypeEnum } from 'common/module/agent/avatar/type.enum';
import { IconTrashTemplate } from 'common/module/icon/trash-template';
import { PropertyLastContactedComponent } from 'common/module/property/last-contacted/component';
import { StatsTealiumDataLayerEventReferenceEnum } from 'common/module/stats/tealium/data-layer/event-reference.enum';
import { UserAuthAsyncComponent } from 'common/module/user/auth/async.component';
import { UserAuthTrackerFactory } from 'common/module/user/auth/tracker.factory';
import { MyAccountToggleSaveButtonComponent } from 'desktop/module/my-account/toggle-save-button/component';
import { PropertyPhoneNumberComponent } from 'desktop/module/property-phone-number/component';

import { PropertyCardSectionsFooterComponentFactoryPropsInterface } from './component-factory-props.interface';
import { PropertyCardSectionsFooterComponentPropsInterface } from './component-props.interface';

export const PropertyCardSectionsFooterComponentFactory = ({
  propertyReportService,
  socialShareViewStore,
}: PropertyCardSectionsFooterComponentFactoryPropsInterface) => {
  const component: FunctionComponent<PropertyCardSectionsFooterComponentPropsInterface> = ({
    className,
    showDeleteButton = false,
    showUserActions = true,
    isSmartad = false,
    isCts = false,
    isImageNotVisible,
    ...props
  }) => {
    const IconShare = useIconRtl(configGetLanguage().isRtl, IconSpriteExternalLink);
    const { onClick, Dropdown } = useDropdownList();
    const [modalVisible, setModalVisible] = useState(false);
    const [isReportClicked, setIsReportClicked] = useState(false);

    const onReportPropertyClicked = useCallback(() => {
      setIsReportClicked(true);
      if (propertyReportService.isUserLoggedIn()) {
        propertyReportService.setProperty(props.property);
        propertyReportService.openPropertyReportModal();
      } else {
        UserAuthTrackerFactory().onLoginModalOpen(StatsTealiumDataLayerEventReferenceEnum.REPORT_PROPERTY);
        setModalVisible(true);
      }
    }, []);

    const onComplete = useCallback(() => {
      setModalVisible(false);
    }, []);

    useEffect(() => {
      if (isReportClicked && propertyReportService.isUserLoggedIn()) {
        propertyReportService.setProperty(props.property);
        propertyReportService.openPropertyReportModal();
        setIsReportClicked(false);
      }
    }, [propertyReportService.getState()?.isLoggedIn]);

    const onClickShare = useCallback(() => {
      socialShareViewStore.initialize({ model: props.property });
      socialShareViewStore.setData(props.property.share_url, null, true);
      socialShareViewStore.showShare(props.property);
    }, []);

    return (
      <footer className={domClassMerge('card-footer', className)}>
        <div className='card-footer__content'>
          {((configGetFeature('trusted_agent').enabled && props.property.agent?.is_trusted) ||
            props.property.areaSpecialistProperty) && (
            <AgentAvatarTemplate
              name={props.property.agent.name}
              image={`${props.property.agent.links.image_desktop}.webp`}
              size={AgentAvatarSizeEnum.small}
              type={props.property.agent.is_trusted ? AgentAvatarTypeEnum.superAgent : null}
              showBorder={props.property.agent.is_trusted}
              visible={!isImageNotVisible}
            />
          )}
          <p className='card-footer__publish-date' aria-label={i18nTranslate('Listed')}>
            {i18nTranslate('Listed')} {props.property.meta.listed_at_message}
            {props.property.areaSpecialistProperty ? ` ${i18nTranslate('by a Community expert')}` : ''}
          </p>
          {props.showActions && !props.isDisabled && (
            <div className='card-footer__buttons'>
              {!!props.property.meta.contact_options.list.phone && (
                <Button
                  className='card-footer__button'
                  onClick={(e: MouseEvent): void => {
                    e.preventDefault();
                    props.onClickCall(e, props.property);
                  }}
                  size={ButtonSizesEnum.SMALL}
                  theme={ButtonThemesEnum.SECONDARY}
                  htmlAttributes={{
                    'data-qs': 'cta',
                  }}
                >
                  <IconSpritePhoneFilled />
                  <PropertyPhoneNumberComponent
                    phoneNumber={props.property.meta.contact_options.list.phone.value}
                    propertyId={props.property.id}
                  />
                </Button>
              )}
              {!!props.property.meta.contact_options.list.email && (
                <Button
                  theme={ButtonThemesEnum.SECONDARY}
                  size={ButtonSizesEnum.SMALL}
                  className='card-footer__button'
                  onClick={(e: MouseEvent): void => {
                    props.onClickEmail(e, props.property);
                  }}
                  htmlAttributes={{
                    'data-qs': 'cta',
                  }}
                >
                  <IconSpriteEnvelopeFilled className='card-footer__button-icon' />
                  {i18nTranslate('cta:email-desktop')}
                </Button>
              )}
              {!!props.property.meta.contact_options.list.whatsapp && (
                <Button
                  className='card-footer__button'
                  onClick={(e: MouseEvent): void => {
                    e.preventDefault();
                    props.onClickWhatsApp(props.property, e);
                  }}
                  size={ButtonSizesEnum.SMALL}
                  theme={ButtonThemesEnum.SECONDARY}
                  htmlAttributes={{
                    'data-qs': 'cta',
                  }}
                >
                  <IconSpriteWhatsappFilled className='card-footer__button-icon' />
                  {i18nTranslate('WhatsApp')}
                </Button>
              )}
              {showUserActions && (
                <Fragment>
                  <div className='card-footer__button-separator' />
                  <MyAccountToggleSaveButtonComponent
                    isSmartad={isSmartad}
                    isCts={isCts}
                    property={props.property}
                    className='card-footer__button'
                  />
                  {showDeleteButton && (
                    <Button
                      size={ButtonSizesEnum.SMALL}
                      theme={ButtonThemesEnum.SECONDARY}
                      className='card-footer__button'
                      onClick={(e: MouseEvent): void => {
                        e.preventDefault();
                        props.onClickDelete(e, props.property);
                      }}
                      htmlAttributes={{
                        'data-qs': 'cta',
                      }}
                    >
                      <IconTrashTemplate class='card-footer__delete-icon' />
                    </Button>
                  )}
                  <Button
                    size={ButtonSizesEnum.SMALL}
                    theme={ButtonThemesEnum.SECONDARY}
                    className='card-footer__button'
                    icon={{ component: IconSpriteThreeDotsVertical, position: 'prefix' }}
                    onClick={(e: MouseEvent): void => {
                      e.preventDefault();
                      e.stopPropagation();
                      onClick(e);
                    }}
                  />
                </Fragment>
              )}
            </div>
          )}
        </div>
        {props.showActions && <PropertyLastContactedComponent propertyId={props.property.id} />}

        <Dropdown
          popoverClassName={'card-footer popover'}
          elements={[
            {
              Icon: IconShare,
              onClick: onClickShare,
              label: i18nTranslate('Share'),
              type: 'button',
            },
            {
              Icon: IconFlag,
              onClick: (): void => onReportPropertyClicked(),
              label: i18nTranslate('actions_buttons:report'),
              type: 'button',
            },
          ]}
          rtl={configGetLanguage().isRtl}
          horizontalPosition='right'
        />

        {modalVisible && (
          <UserAuthAsyncComponent
            customModalTitle={'login-modal-report-property-title'}
            customModalDescription={'login-modal-report-property-vp'}
            customModalImage={__('assets/common/report-property.svg')}
            reference={StatsTealiumDataLayerEventReferenceEnum.REPORT_PROPERTY}
            onSuccess={onComplete}
            onClose={onComplete}
          />
        )}
      </footer>
    );
  };

  return component;
};
